'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp, X, MessageSquare, AlertTriangle } from 'lucide-react';
import AppLayout from '@/components/AppLayout';
import { getUnreadAlertCount, getUnreadMessageCount } from '@/lib/demoData';

const FAQ_ITEMS = [
  {
    id: 'faq-1',
    question: 'Comment appeler André ?',
    answer: 'Depuis l\'accueil, appuyez sur le bouton « Appeler André » ou sur son portrait. Choisissez ensuite entre un appel vidéo ou audio. André recevra une notification sur sa tablette.',
  },
  {
    id: 'faq-2',
    question: 'Comment répondre à une demande de rappel ?',
    answer: 'Lorsqu\'André souhaite être appelé, une alerte apparaît dans l\'onglet Alertes et sur la page d\'accueil. Appuyez sur « Rappeler André » pour lancer l\'appel directement.',
  },
  {
    id: 'faq-3',
    question: 'Comment envoyer une photo ?',
    answer: 'Appuyez sur l\'onglet « Envoyer » dans la navigation inférieure, puis sélectionnez « Photo ». Choisissez une photo depuis votre galerie et ajoutez un message si vous le souhaitez avant d\'envoyer.',
  },
  {
    id: 'faq-4',
    question: 'Comment ajouter une visite dans l\'agenda ?',
    answer: 'Ouvrez l\'onglet « Agenda », appuyez sur le bouton « + » ou sur une date. Remplissez les informations de la visite (date, heure, note) et enregistrez.',
  },
  {
    id: 'faq-5',
    question: 'Pourquoi la tablette apparaît-elle hors ligne ?',
    answer: 'La tablette d\'André peut apparaître hors ligne si elle est éteinte, en veille prolongée ou si la connexion internet est interrompue. Une alerte vous est envoyée automatiquement. La tablette se reconnecte généralement d\'elle-même.',
  },
  {
    id: 'faq-6',
    question: 'Comment ajouter un proche autorisé ?',
    answer: 'Rendez-vous dans Paramètres > Contacts et autorisations, puis appuyez sur « + Ajouter un proche ». Renseignez les informations de la personne et choisissez ses droits. Une invitation lui sera envoyée.',
  },
];

type ModalType = 'contact' | 'report' | null;

export default function HelpClient() {
  const router = useRouter();
  const unreadAlerts = getUnreadAlertCount();
  const unreadMessages = getUnreadMessageCount();

  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalType>(null);

  // Contact form state
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSent, setContactSent] = useState(false);

  // Report form state
  const [reportType, setReportType] = useState('');
  const [reportScreen, setReportScreen] = useState('');
  const [reportDesc, setReportDesc] = useState('');
  const [reportSent, setReportSent] = useState(false);

  const closeModal = () => {
    setModal(null);
    setContactSent(false);
    setReportSent(false);
    setContactSubject('');
    setContactMessage('');
    setReportType('');
    setReportScreen('');
    setReportDesc('');
  };

  const handleContactSend = () => {
    setContactSent(true);
    setTimeout(() => closeModal(), 2500);
  };

  const handleReportSend = () => {
    setReportSent(true);
    setTimeout(() => closeModal(), 2500);
  };

  return (
    <AppLayout unreadAlerts={unreadAlerts} unreadMessages={unreadMessages}>
      {/* Sub-header */}
      <div className="bg-lucat-navy px-4 pt-4 pb-5">
        <button
          onClick={() => router.push('/settings')}
          className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium mb-4 transition-colors"
          style={{ minHeight: 44 }}
          aria-label="Retour aux paramètres"
        >
          <ArrowLeft size={18} />
          Retour
        </button>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <HelpCircle size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Aide et support</h1>
            <p className="text-sm text-white/70 mt-0.5">Trouvez une réponse ou contactez l'assistance</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-5 space-y-4 pb-8">
        {/* FAQ */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 pt-4 pb-2">
            <h2 className="text-sm font-bold text-lucat-navy">Questions fréquentes</h2>
          </div>
          {FAQ_ITEMS.map((item, idx) => (
            <div key={item.id} className={idx < FAQ_ITEMS.length - 1 ? 'border-b border-gray-100' : ''}>
              <button
                onClick={() => setOpenFaq(openFaq === item.id ? null : item.id)}
                className="w-full flex items-center justify-between px-4 py-4 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
                style={{ minHeight: 52 }}
                aria-expanded={openFaq === item.id}
                aria-label={item.question}
              >
                <span className="text-sm font-semibold text-gray-800 pr-3 flex-1">{item.question}</span>
                {openFaq === item.id
                  ? <ChevronUp size={18} className="text-lucat-navy flex-shrink-0" />
                  : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />
                }
              </button>
              {openFaq === item.id && (
                <div className="px-4 pb-4">
                  <p className="text-sm text-gray-600 leading-relaxed">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Support section */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h2 className="text-sm font-bold text-lucat-navy mb-3">Besoin d'aide supplémentaire ?</h2>
          <div className="space-y-2">
            <button
              onClick={() => setModal('contact')}
              className="w-full flex items-center gap-3 bg-lucat-green text-white rounded-2xl px-4 py-4 hover:bg-lucat-green/90 active:scale-95 transition-all"
              style={{ minHeight: 52 }}
              aria-label="Contacter l'assistance LUCAT CONNECT"
            >
              <MessageSquare size={18} className="flex-shrink-0" />
              <span className="font-bold text-sm">Contacter l'assistance</span>
            </button>
            <button
              onClick={() => setModal('report')}
              className="w-full flex items-center gap-3 bg-gray-100 text-gray-700 rounded-2xl px-4 py-4 hover:bg-gray-200 active:scale-95 transition-all"
              style={{ minHeight: 52 }}
              aria-label="Signaler un problème dans l'application"
            >
              <AlertTriangle size={18} className="flex-shrink-0" />
              <span className="font-bold text-sm">Signaler un problème</span>
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {modal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md mx-0 sm:mx-4 max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Contact modal */}
            {modal === 'contact' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-lucat-navy">Contacter l'assistance</h3>
                  <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-100" aria-label="Fermer">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>
                {contactSent ? (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 bg-lucat-green/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <MessageSquare size={24} className="text-lucat-green" />
                    </div>
                    <p className="text-sm font-semibold text-gray-800">Votre demande a été envoyée à l'assistance.</p>
                    <p className="text-xs text-gray-500 mt-1">Nous vous répondrons dans les meilleurs délais.</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 mb-5">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1" htmlFor="contact-subject">Sujet</label>
                        <input
                          id="contact-subject"
                          type="text"
                          value={contactSubject}
                          onChange={e => setContactSubject(e.target.value)}
                          placeholder="Ex : Problème de connexion"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-lucat-green"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1" htmlFor="contact-message">Message</label>
                        <textarea
                          id="contact-message"
                          value={contactMessage}
                          onChange={e => setContactMessage(e.target.value)}
                          placeholder="Décrivez votre demande..."
                          rows={4}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-lucat-green resize-none"
                        />
                      </div>
                    </div>
                    <button
                      onClick={handleContactSend}
                      className="w-full bg-lucat-green text-white font-bold text-sm rounded-2xl py-4 hover:bg-lucat-green/90 active:scale-95 transition-all"
                      aria-label="Envoyer ma demande à l'assistance"
                    >
                      Envoyer ma demande
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Report modal */}
            {modal === 'report' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-lucat-navy">Signaler un problème</h3>
                  <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-100" aria-label="Fermer">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>
                {reportSent ? (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <AlertTriangle size={24} className="text-orange-500" />
                    </div>
                    <p className="text-sm font-semibold text-gray-800">Votre signalement a été envoyé.</p>
                    <p className="text-xs text-gray-500 mt-1">Merci de nous aider à améliorer l'application.</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 mb-5">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1" htmlFor="report-type">Type de problème</label>
                        <select
                          id="report-type"
                          value={reportType}
                          onChange={e => setReportType(e.target.value)}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-lucat-green bg-white"
                        >
                          <option value="">Sélectionner...</option>
                          <option value="bug">Erreur ou bug</option>
                          <option value="display">Problème d'affichage</option>
                          <option value="call">Problème d'appel</option>
                          <option value="message">Problème de messagerie</option>
                          <option value="other">Autre</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1" htmlFor="report-screen">Écran concerné</label>
                        <input
                          id="report-screen"
                          type="text"
                          value={reportScreen}
                          onChange={e => setReportScreen(e.target.value)}
                          placeholder="Ex : Page d'accueil, Appel vidéo..."
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-lucat-green"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1" htmlFor="report-desc">Description</label>
                        <textarea
                          id="report-desc"
                          value={reportDesc}
                          onChange={e => setReportDesc(e.target.value)}
                          placeholder="Décrivez le problème rencontré..."
                          rows={4}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-lucat-green resize-none"
                        />
                      </div>
                    </div>
                    <button
                      onClick={handleReportSend}
                      className="w-full bg-lucat-navy text-white font-bold text-sm rounded-2xl py-4 hover:bg-lucat-navy/90 active:scale-95 transition-all"
                      aria-label="Envoyer le signalement"
                    >
                      Envoyer le signalement
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
